import { useMandateSummary } from "../hooks/useMandateSummary.ts";

// Same hardcoded palette as QuoteQualityCard / the box-whisker chart.
const TEXT = "#cfe0ff";
const MUTED = "#8a93a6";
const AMBER = "#e0a458";
const GREEN = "#6fcf97";
const BORDER = "rgba(255,255,255,0.08)";
const CARD_BG = "rgba(255,255,255,0.03)";

// Capture level band (backend labels.classify_capture_level) -> chip + headline
// color. Negative retention is flagged amber but the magnitude carries in the
// number itself, so a small -0.04 reads as a mild caution, not alarm.
const LEVEL: Record<string, { text: string; color: string }> = {
  negative: { text: "negative retention", color: AMBER },
  thin: { text: "thin capture", color: MUTED },
  fair: { text: "fair capture", color: GREEN },
  rich: { text: "strong capture", color: GREEN },
  unknown: { text: "n/a", color: MUTED },
};

// Capture trajectory (backend labels.classify_capture_trend). Narrowing capture
// toward zero is the underwriting tell; shown plainly without that phrasing.
const TREND: Record<string, { text: string; color: string }> = {
  deteriorating: { text: "deteriorating", color: AMBER },
  stable: { text: "stable", color: MUTED },
  improving: { text: "improving", color: GREEN },
  unknown: { text: "n/a", color: MUTED },
};

function fmt(n: number | null | undefined, digits = 2): string {
  return n == null ? "—" : n.toFixed(digits);
}

function fmtN(n: number | null | undefined): string {
  return n == null ? "—" : n.toLocaleString();
}

export function SpreadCaptureCard({
  venue,
  pair,
  clientId,
}: {
  venue: string;
  pair: string;
  clientId?: string;
}) {
  const q = useMandateSummary({ venue, pair, clientId });
  const sc = q.data?.spread_capture;

  const level = LEVEL[sc?.level ?? "unknown"] ?? LEVEL.unknown;
  const trend = TREND[sc?.trend ?? "unknown"] ?? TREND.unknown;

  // Headline = 30d volume-weighted realized capture. Number is neutral TEXT;
  // the level band signal is carried by the chip (consistent across cards).
  const headline = sc?.realized_30d_bps;

  return (
    <div
      style={{
        background: CARD_BG,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        padding: "12px 14px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 12, color: MUTED }}>Spread capture</span>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: MUTED }}>30d</span>
          <span
            style={{
              fontSize: 11,
              padding: "2px 8px",
              borderRadius: 5,
              color: level.color,
              border: `1px solid ${level.color}55`,
            }}
            title="Negative retention means realized spread capture is below zero over the selected horizon."
          >
            {level.text}
          </span>
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span
          style={{
            fontSize: 24,
            fontWeight: 500,
            color: TEXT,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {fmt(headline)}
        </span>
        <span style={{ fontSize: 12, color: MUTED }}>bps capture</span>
      </div>

      <div style={{ fontSize: 12, color: MUTED, marginTop: 6, lineHeight: 1.5 }}>
        trend <span style={{ color: trend.color }}>{trend.text}</span>
        <br />
        <span style={{ fontVariantNumeric: "tabular-nums" }}>
          1d {fmt(sc?.realized_1d_bps)} · 7d {fmt(sc?.realized_7d_bps)}
        </span>
      </div>

      <div
        style={{
          fontSize: 11,
          color: MUTED,
          marginTop: 8,
          borderTop: `1px solid ${BORDER}`,
          paddingTop: 7,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        trades 1d {fmtN(sc?.n_1d)} · 7d {fmtN(sc?.n_7d)} · 30d {fmtN(sc?.n_30d)}
      </div>

      {q.isError && (
        <div style={{ fontSize: 11, color: AMBER, marginTop: 6 }}>
          could not load
        </div>
      )}
    </div>
  );
}
